// Summarize complete accounting extracts without inferring a tax rate or converting currency.
// These are recorded sales and tax charges for review, not amounts to copy to a filed return.
const NATIVE = "Ranch Power";
const SHOPIFY = "Shopify";
const COMBINED = "Combined";
const SHOPIFY_DATASETS = ["sales", "lines", "taxes", "refunds"];
const NON_REVENUE_LINE_CATEGORIES = new Set(["tax_adjustment", "deposit", "account_adjustment", "gift_card"]);

function text(value) { return String(value ?? "").trim(); }
function cents(value, label) {
  if (value == null || value === "" || !Number.isFinite(Number(value))) throw new Error(`${label} is missing or invalid. Restart the summary.`);
  return Math.round(Number(value) * 100);
}
function optionalCents(value, label) { return value == null || value === "" ? 0 : cents(value, label); }
function amount(value) { return Math.round(value) / 100; }
function currencyOf(row, label) {
  const currency = text(row.currency).toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency) || currency === "UNK") throw new Error(`${label} has no known source currency. Restart the summary.`);
  return currency;
}
function dateInPeriod(row, filters, label) {
  const date = text(row.date);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < filters.dateFrom || date > filters.dateTo) {
    throw new Error(`${label} has a date outside the selected period. Restart the summary.`);
  }
}
function shopifyOrderId(value) {
  const match = /^(?:gid:\/\/shopify\/Order\/)?([1-9]\d*)$/.exec(text(value));
  return match ? `gid://shopify/Order/${match[1]}` : "";
}
function orderKey(row) {
  const orderId = shopifyOrderId(row.shopifyOrderId);
  if (!orderId) throw new Error("A Shopify accounting row is missing its order ID. Restart the summary.");
  return `${text(row.shopDomain).toLowerCase()}|${orderId}`;
}
function requiredSheet(sheets, id, endpoint = "report") {
  const sheet = sheets.find(item => item?.report?.id === id && (item.report.endpoint || "report") === endpoint);
  if (!sheet || !Array.isArray(sheet.rows) || sheet.complete !== true || ["incomplete", "unavailable"].includes(sheet.metadata?.coverage?.status)) {
    throw new Error(`${id} is missing or incomplete. No monthly summary was made.`);
  }
  return sheet;
}
function taxKind(value) {
  const codes = [...new Set(text(value).toUpperCase().match(/\b(?:GST|PST|HST|QST|RST)\b/g) || [])];
  return codes.length === 1 ? codes[0] : "";
}
function recordReference(row) { return text(row.invoiceNumber || row.orderNumber || row.orderId || row.shopifyOrderId || row.id); }

/**
 * @param {{sheets: Array<{report:{id:string,endpoint?:string,dataset?:string},rows:object[],complete:boolean,metadata?:object}>,filters:{dateFrom:string,dateTo:string},includeShopify?:boolean,crossHistoryVerified?:boolean}} input
 * @returns {{totals: Array<{source:string,currency:string,grossRevenue:number,pstCharged:number,gstCharged:number,recordCount:number,basis:string}>,exceptions:Array<{code:string,source:string,currency:string,count:number,amount:number|null,message:string,examples:string[]}>,sourceReconciliation:Array<{nativeOrderId:string,nativeInvoiceNumber:string,shopifyOrderId:string,shopifySaleDate:string,status:string,countedSource:string}>,sourceLinksReady:boolean,crossHistoryVerified:boolean,periodLabel:string}}
 */
export function buildAccountingMonthlySummary({ sheets, filters, includeShopify = true,
  crossHistoryVerified = false, reviewedMirrors = [], reviewedNativeOnly = [], deferredNativeOrderIds = [] }) {
  if (!Array.isArray(sheets) || !filters || !/^\d{4}-\d{2}-\d{2}$/.test(text(filters.dateFrom)) || !/^\d{4}-\d{2}-\d{2}$/.test(text(filters.dateTo)) || filters.dateFrom > filters.dateTo) {
    throw new Error("Choose a valid period before preparing a monthly summary.");
  }
  const income = requiredSheet(sheets, "income").rows;
  const charges = requiredSheet(sheets, "charges").rows;
  const shopify = includeShopify ? Object.fromEntries(SHOPIFY_DATASETS.map(dataset => [dataset, requiredSheet(sheets, `shopify_${dataset}`, "shopify").rows])) : null;
  const totals = new Map();
  const exceptionMap = new Map();
  const sourceReconciliation = [];
  const nativeMirrors = [];
  const countedNativeOrderIds = new Set();
  const deferredIds = new Set(deferredNativeOrderIds);
  const deferredRows = new Map();
  if (!Array.isArray(deferredNativeOrderIds) || deferredIds.size !== deferredNativeOrderIds.length
    || (deferredIds.size && crossHistoryVerified !== true)) {
    throw new Error("Deferred invoice identities require a complete source-history check.");
  }
  if (!Array.isArray(reviewedMirrors) || !Array.isArray(reviewedNativeOnly)
    || ((reviewedMirrors.length || reviewedNativeOnly.length) && crossHistoryVerified !== true)) {
    throw new Error("Reviewed Shopify mirrors require a complete source-history check.");
  }
  const reviewedByNative = new Map();
  for (const review of reviewedMirrors) {
    const id = text(review.nativeOrderId), source = shopifyOrderId(review.shopifyOrderId);
    if (!id || !source || reviewedByNative.has(id)) throw new Error("Reviewed Shopify mirror evidence is invalid.");
    reviewedByNative.set(id, source);
  }
  const distinctByNative = new Map();
  for (const review of reviewedNativeOnly) {
    const id = text(review.nativeOrderId);
    if (!id || distinctByNative.has(id) || reviewedByNative.has(id)) {
      throw new Error("Reviewed native-only evidence is invalid.");
    }
    distinctByNative.set(id, review);
  }
  let combinedAllowed = crossHistoryVerified === true;
  // This covers ambiguity visible in this period. Prepared artifacts also run
  // a full-history preflight to resolve valid links into other sale months.
  let sourceLinksReady = true;
  const addTotal = (source, currency, field, value) => {
    const key = `${source}|${currency}`;
    if (!totals.has(key)) totals.set(key, { source, currency, grossRevenue: 0, pstCharged: 0, gstCharged: 0, recordCount: 0,
      basis: source === NATIVE ? "Issued native invoices; before tax, deposits and previous balances; includes recorded sale fees."
        : "Eligible Shopify orders before refunds; before tax and recorded non-revenue lines; includes other order charges." });
    totals.get(key)[field] += value;
  };
  const addException = (code, source, currency, message, row = null, value = null) => {
    const key = `${code}|${source}|${currency}|${message}`;
    if (!exceptionMap.has(key)) exceptionMap.set(key, { code, source, currency, count: 0, amount: null, message, examples: [] });
    const entry = exceptionMap.get(key);
    entry.count += 1;
    if (value != null) entry.amount = (entry.amount || 0) + value;
    const reference = row && recordReference(row);
    if (reference && entry.examples.length < 5 && !entry.examples.includes(reference)) entry.examples.push(reference);
  };
  if (includeShopify && !crossHistoryVerified) addException("cross_history_not_verified", COMBINED, "CAD",
    "Combined totals require a complete cross-history Shopify and Ranch Power overlap check. Review the separate source figures until a prepared workbook is available.");

  // An exact Shopify order pointer always wins over the treatment label: a
  // future SQL regression must never count the same sale as native income too.
  const invoiceByOrder = new Map();
  for (const row of income) {
    dateInPeriod(row, filters, "Native income");
    const id = text(row.orderId || row.id);
    if (!id || invoiceByOrder.has(id)) throw new Error("Native income has a missing or repeated order ID. Restart the summary.");
    invoiceByOrder.set(id, row);
    const treatment = text(row.accountingTreatment);
    const sourcePointer = text(row.shopifyOrderId);
    const reviewedPointer = reviewedByNative.get(id) || "";
    const distinctReview = distinctByNative.get(id);
    if (deferredIds.has(id)) {
      if (sourcePointer || treatment === "shopify_source" || reviewedPointer || distinctReview) {
        throw new Error("A linked or reviewed invoice cannot be deferred as an unresolved source.");
      }
      const currency = currencyOf(row, "Deferred native invoice");
      const gross = cents(row.total, "Deferred invoice total") - cents(row.tax, "Deferred invoice tax")
        - optionalCents(row.carriedDebt, "Previous balance") - optionalCents(row.deposits, "Deferred invoice deposits");
      if (gross < 0) throw new Error(`Deferred invoice ${id} has a negative pre-tax sales basis.`);
      deferredRows.set(id, { nativeOrderId: id, invoiceNumber: text(row.invoiceNumber), date: text(row.date),
        currency, grossRevenueCents: gross, pstChargedCents: 0, gstChargedCents: 0,
        status: "Excluded from this month's available figures; resolve for the next filing" });
      sourceReconciliation.push({ nativeOrderId: id, nativeInvoiceNumber: text(row.invoiceNumber),
        shopifyOrderId: "", shopifySaleDate: "", status: "Deferred for next-filing source review",
        countedSource: "Native invoice excluded; eligible Shopify POS orders counted once" });
      addException("deferred_native_invoice", NATIVE, currency,
        "This unresolved native invoice and its recorded GST/PST are excluded from the available monthly figures and listed for next-filing review.", row, gross);
      continue;
    }
    if (distinctReview && (sourcePointer || treatment !== "native_income")) {
      throw new Error("Reviewed native-only sale conflicts with source treatment.");
    }
    if (reviewedPointer && (sourcePointer || treatment !== "native_income")) {
      throw new Error("Reviewed Shopify mirror conflicts with native source treatment.");
    }
    const pointer = sourcePointer || reviewedPointer;
    if (pointer || treatment === "shopify_source") {
      addException("native_mirror_excluded", NATIVE, text(row.currency) || "UNKNOWN", "Shopify-linked native invoices are source evidence and excluded from native totals.", row);
      const canonicalId = shopifyOrderId(pointer);
      nativeMirrors.push({ row, id, canonicalId, reviewed: Boolean(reviewedPointer) });
      if (!canonicalId || (!reviewedPointer && treatment !== "shopify_source")) {
        combinedAllowed = false;
        sourceLinksReady = false;
        addException("conflicting_source_link", NATIVE, text(row.currency) || "UNKNOWN", "A native invoice has a missing, malformed or inconsistently classified Shopify order link. It was excluded; reconcile the source before combining totals.", row);
      }
      continue;
    }
    if (treatment !== "native_income") {
      combinedAllowed = false;
      sourceLinksReady = false;
      addException("unverified_native_link", NATIVE, text(row.currency) || "UNKNOWN", "Native source treatment needs reconciliation before counting income or tax.", row);
      sourceReconciliation.push({ nativeOrderId: id, nativeInvoiceNumber: text(row.invoiceNumber), shopifyOrderId: pointer,
        shopifySaleDate: "", status: "Unverified possible Shopify link", countedSource: "Neither until reviewed" });
      continue;
    }
    const currency = currencyOf(row, "Native income");
    const gross = cents(row.total, "Native invoice total") - cents(row.tax, "Native invoice tax")
      - optionalCents(row.carriedDebt, "Previous balance") - optionalCents(row.deposits, "Native deposits");
    if (gross < 0) throw new Error(`Native invoice ${id} has a negative pre-tax sales basis. Review the source invoice.`);
    addTotal(NATIVE, currency, "grossRevenue", gross);
    addTotal(NATIVE, currency, "recordCount", 1);
    countedNativeOrderIds.add(id);
    if (distinctReview) sourceReconciliation.push({ nativeOrderId: id,
      nativeInvoiceNumber: text(row.invoiceNumber), shopifyOrderId: "", shopifySaleDate: "",
      status: "Reviewed distinct native sale", countedSource: NATIVE,
      evidenceReference: text(distinctReview.evidenceReference), reviewedBy: text(distinctReview.reviewedBy) });
    if (optionalCents(row.deposits, "Native deposits")) addException("non_revenue_line_review", NATIVE, currency, "Recorded deposits were excluded from gross sales and need accounting treatment review.", row, optionalCents(row.deposits, "Native deposits"));
  }
  if (reviewedByNative.size !== nativeMirrors.filter(item => item.reviewed).length) {
    throw new Error("A reviewed Shopify mirror is absent from the native income extract.");
  }
  if (distinctByNative.size !== sourceReconciliation.filter(row => row.status === "Reviewed distinct native sale").length) {
    throw new Error("A reviewed native-only sale is absent from the native income extract.");
  }
  if (deferredRows.size !== deferredIds.size) throw new Error("A deferred invoice is absent from the native income extract.");
  const mirrorCounts = new Map();
  for (const mirror of nativeMirrors) if (mirror.canonicalId) mirrorCounts.set(mirror.canonicalId, (mirrorCounts.get(mirror.canonicalId) || 0) + 1);
  for (const mirror of nativeMirrors) if (mirror.canonicalId && mirrorCounts.get(mirror.canonicalId) > 1) {
    combinedAllowed = false;
    sourceLinksReady = false;
    addException("duplicate_native_mirror", NATIVE, text(mirror.row.currency) || "UNKNOWN", "Multiple native invoices point to one Shopify order; all native copies were excluded from sales and tax totals.", mirror.row);
  }
  // A complete charges extract retains any difference from invoice tax as an
  // explicit UNALLOCATED_TAX row. Never assign that residual to GST or PST.
  for (const row of charges) {
    dateInPeriod(row, filters, "Native charges");
    const invoice = invoiceByOrder.get(text(row.orderId));
    if (!invoice) {
      addException("orphan_native_charge", NATIVE, text(row.currency) || "UNKNOWN", "A charge has no matching issued invoice in this period.", row, optionalCents(row.amount, "Native charge"));
      continue;
    }
    const deferred = deferredRows.get(text(row.orderId));
    if (deferred) {
      if (text(row.type).toLowerCase() === "tax") {
        const value = cents(row.amount, "Deferred native tax adjustment");
        const kind = taxKind(row.code);
        if (kind === "GST") deferred.gstChargedCents += value;
        else if (kind === "PST") deferred.pstChargedCents += value;
        else addException("deferred_unallocated_tax", NATIVE, deferred.currency,
          "A deferred invoice has tax that cannot be classified as GST or PST; inspect before next filing.", row, value);
      }
      continue;
    }
    if (!countedNativeOrderIds.has(text(row.orderId))) continue;
    const currency = currencyOf(invoice, "Native income");
    if (text(row.type).toLowerCase() !== "tax") {
      if (["GST", "PST"].includes(text(row.code).toUpperCase())) addException("inconsistent_tax_type", NATIVE, currency, "A GST/PST code was recorded as a non-tax adjustment and was not classified.", row, optionalCents(row.amount, "Native adjustment"));
      continue;
    }
    const value = cents(row.amount, "Native tax adjustment");
    const kind = taxKind(row.code);
    if (kind === "GST") addTotal(NATIVE, currency, "gstCharged", value);
    else if (kind === "PST") {
      addTotal(NATIVE, currency, "pstCharged", value);
      const region = text(row.region || row.taxRegion || row.metadata?.region).toUpperCase();
      if (!region) addException("missing_tax_jurisdiction", NATIVE, currency, "PST was charged, but the report does not expose the recorded province; confirm BC eligibility.", row, value);
      else if (region !== "BC") addException("pst_other_region", NATIVE, currency, `PST was charged for ${region}; separate it from BC PST filing review.`, row, value);
    } else {
      combinedAllowed = false;
      addException(text(row.code).toUpperCase() === "UNALLOCATED_TAX" ? "native_unallocated_tax" : "other_or_unknown_tax", NATIVE, currency,
        kind ? `${kind} tax is recorded separately from GST/PST.` : "Native tax could not be assigned to GST or PST from its recorded code.", row, value);
    }
  }

  if (shopify) {
    const saleByOrder = new Map();
    const excludedByOrder = new Map();
    const providerTaxByOrder = new Map();
    const nativeOrderIds = countedNativeOrderIds;
    const nativeInvoiceNumbers = new Set(income.filter(row => countedNativeOrderIds.has(text(row.orderId || row.id))).map(row => text(row.invoiceNumber)).filter(Boolean));
    const nativeByInvoice = new Map(income.filter(row => countedNativeOrderIds.has(text(row.orderId || row.id)) && text(row.invoiceNumber))
      .map(row => [text(row.invoiceNumber), row]));
    const saleById = new Map();
    for (const row of shopify.sales) {
      dateInPeriod(row, filters, "Shopify sale");
      const key = orderKey(row);
      if (saleByOrder.has(key)) throw new Error("Shopify sales repeat an order ID. Restart the summary.");
      saleByOrder.set(key, row);
      const canonicalId = shopifyOrderId(row.shopifyOrderId);
      if (saleById.has(canonicalId)) throw new Error("Shopify sales repeat an order ID across source representations. Restart the summary.");
      saleById.set(canonicalId, row);
      const hintedNative = nativeOrderIds.has(text(row.nativeOrderIdHint)) && text(row.nativeOrderIdHint)
        ? invoiceByOrder.get(text(row.nativeOrderIdHint))
        : nativeInvoiceNumbers.has(text(row.nativeInvoiceNumberHint)) && text(row.nativeInvoiceNumberHint)
          ? nativeByInvoice.get(text(row.nativeInvoiceNumberHint)) : null;
      if (hintedNative) {
        combinedAllowed = false;
        sourceLinksReady = false;
        addException("possible_source_duplicate", SHOPIFY, text(row.currency) || "UNKNOWN", "A Shopify reference names a native invoice without a verified source link. Reconcile before combining sources.", row);
        sourceReconciliation.push({ nativeOrderId: text(hintedNative.orderId || hintedNative.id), nativeInvoiceNumber: text(hintedNative.invoiceNumber),
          shopifyOrderId: canonicalId, shopifySaleDate: text(row.date), status: "Shopify hint names unlinked native invoice",
          countedSource: "Both source columns; combined withheld" });
      }
      if (row.recognizedSale !== true || row.cancelledAt) addException("cancelled_shopify_excluded", SHOPIFY, text(row.currency) || "UNKNOWN", "Cancelled or voided Shopify orders and their order tax were excluded.", row);
    }
    for (const mirror of nativeMirrors) {
      const sale = mirror.canonicalId ? saleById.get(mirror.canonicalId) : null;
      const duplicates = mirror.canonicalId && mirrorCounts.get(mirror.canonicalId) > 1;
      if (mirror.canonicalId && !sale) addException("linked_shopify_sale_not_in_period", NATIVE, text(mirror.row.currency) || "UNKNOWN",
        crossHistoryVerified ? "A linked Shopify sale is outside this invoice month; its direct link was verified by the full-history overlap check."
          : "A linked Shopify sale is absent from this month's Shopify extract. Check whether its sale date falls in another month.", mirror.row);
      if (sale && (sale.recognizedSale !== true || sale.cancelledAt)) {
        combinedAllowed = false;
        sourceLinksReady = false;
        addException("linked_shopify_sale_not_recognized", NATIVE, text(mirror.row.currency) || "UNKNOWN", "A native invoice links to a cancelled or unrecognized Shopify sale. Reconcile the source before combining totals.", mirror.row);
      }
      sourceReconciliation.push({ nativeOrderId: mirror.id, nativeInvoiceNumber: text(mirror.row.invoiceNumber), shopifyOrderId: mirror.canonicalId || text(mirror.row.shopifyOrderId),
        shopifySaleDate: sale ? text(sale.date) : "", status: !mirror.canonicalId ? "Invalid direct link" : sale ? duplicates ? "Matched; multiple native mirrors" : sale.recognizedSale !== true || sale.cancelledAt ? "Matched cancelled or unrecognized sale" : mirror.reviewed ? "Reviewed mirror; matched in this month" : "Matched in this month" : crossHistoryVerified ? mirror.reviewed ? "Reviewed mirror; sale in another month" : "Linked sale in another month (verified)" : "Linked sale outside month or missing",
        countedSource: sale?.recognizedSale === true && !sale?.cancelledAt ? SHOPIFY : "Neither in this month",
        evidenceReference: mirror.reviewed ? text(reviewedMirrors.find(review => text(review.nativeOrderId) === mirror.id)?.evidenceReference) : "",
        reviewedBy: mirror.reviewed ? text(reviewedMirrors.find(review => text(review.nativeOrderId) === mirror.id)?.reviewedBy) : "" });
    }
    for (const row of shopify.lines) {
      dateInPeriod(row, filters, "Shopify line");
      const key = orderKey(row);
      const sale = saleByOrder.get(key);
      if (!sale) {
        addException("shopify_order_missing", SHOPIFY, text(row.currency) || "UNKNOWN", "A Shopify line has no matching sale in the selected period.", row);
        continue;
      }
      if (sale.recognizedSale !== true || sale.cancelledAt) continue;
      const category = text(row.category).toLowerCase();
      if (!NON_REVENUE_LINE_CATEGORIES.has(category)) continue;
      const currency = currencyOf(sale, "Shopify sale");
      const value = cents(row.netBeforeTax, "Shopify non-revenue line");
      excludedByOrder.set(key, (excludedByOrder.get(key) || 0) + value);
      if (category !== "tax_adjustment") addException("non_revenue_line_review", SHOPIFY, currency, `${category.replace(/_/g, " ")} lines were excluded from gross sales and need accounting treatment review.`, row, value);
    }
    for (const [key, sale] of saleByOrder) {
      if (sale.recognizedSale !== true || sale.cancelledAt) continue;
      const currency = currencyOf(sale, "Shopify sale");
      const tips = optionalCents(sale.tips, "Shopify tips");
      const gross = cents(sale.total, "Shopify sale total") - cents(sale.tax, "Shopify provider tax") - tips - (excludedByOrder.get(key) || 0);
      if (gross < 0) throw new Error(`Shopify order ${recordReference(sale)} has a negative pre-tax sales basis. Review the source order.`);
      addTotal(SHOPIFY, currency, "grossRevenue", gross);
      addTotal(SHOPIFY, currency, "recordCount", 1);
      if (tips) addException("non_revenue_line_review", SHOPIFY, currency, "Tips were excluded from gross sales and need accounting treatment review.", sale, tips);
      if (sale.edited === true) addException("edited_shopify_sale", SHOPIFY, currency, "An edited Shopify order uses current source values; review its change history.", sale);
    }
    for (const row of shopify.taxes) {
      dateInPeriod(row, filters, "Shopify tax");
      const currency = currencyOf(row, "Shopify tax");
      const value = cents(row.amount, "Shopify tax amount");
      const scope = text(row.scope).toLowerCase();
      const allocation = text(row.allocation).toLowerCase();
      if (scope.startsWith("refund")) {
        addException("refund_tax_unallocated", SHOPIFY, currency,
          allocation === "explicit_tax_charge_refund"
            ? "An explicit tax-charge refund is shown separately; charged tax totals are before refunds."
            : "Provider refund tax has no recorded GST/PST allocation and was not netted against charged tax.", row, value);
        continue;
      }
      const sale = saleByOrder.get(orderKey(row));
      if (!sale) {
        addException("shopify_order_missing", SHOPIFY, currency, "Shopify order tax has no matching sale in the selected period.", row, value);
        continue;
      }
      if (sale.recognizedSale !== true || sale.cancelledAt) continue;
      if (scope === "order") providerTaxByOrder.set(orderKey(row), (providerTaxByOrder.get(orderKey(row)) || 0) + value);
      const kind = taxKind(row.taxTitle);
      if (kind === "GST") addTotal(SHOPIFY, currency, "gstCharged", value);
      else if (kind === "PST") addTotal(SHOPIFY, currency, "pstCharged", value);
      else {
        combinedAllowed = false;
        addException("other_or_unknown_tax", SHOPIFY, currency, "Shopify tax title does not identify a single GST or PST component.", row, value);
      }
    }
    for (const [key, sale] of saleByOrder) {
      if (sale.recognizedSale !== true || sale.cancelledAt) continue;
      const difference = cents(sale.tax, "Shopify provider tax") - (providerTaxByOrder.get(key) || 0);
      if (Math.abs(difference) > 1) {
        combinedAllowed = false;
        addException("shopify_unallocated_tax", SHOPIFY, currencyOf(sale, "Shopify sale"), "Shopify order tax lines do not reconcile to its recorded provider tax total; GST/PST classification is incomplete.", sale, difference);
      }
    }
    for (const row of shopify.refunds) {
      dateInPeriod(row, filters, "Shopify refund");
      if (text(row.recordType).toLowerCase() === "summary") addException("refunds_not_net_revenue", SHOPIFY, text(row.currency) || "UNKNOWN", "Gross sales are before refunds; review refunds and credit notes separately.", row, optionalCents(row.amount, "Shopify refund"));
    }
  }
  if (!shopify) for (const mirror of nativeMirrors) sourceReconciliation.push({ nativeOrderId: mirror.id,
    nativeInvoiceNumber: text(mirror.row.invoiceNumber), shopifyOrderId: mirror.canonicalId || text(mirror.row.shopifyOrderId),
    shopifySaleDate: "", status: !mirror.canonicalId ? "Invalid direct link" : mirrorCounts.get(mirror.canonicalId) > 1 ? "Multiple native mirrors; Shopify not included" : "Shopify not included in export", countedSource: "Neither in this export" });

  // Never mix currencies. A combined line exists only when both complete
  // source extracts have an actual row in the same known currency.
  if (!totals.has(`${NATIVE}|CAD`)) addTotal(NATIVE, "CAD", "grossRevenue", 0);
  if (shopify && combinedAllowed) for (const [key, native] of totals) {
    if (!key.startsWith(`${NATIVE}|`)) continue;
    const other = totals.get(`${SHOPIFY}|${native.currency}`);
    if (!other) continue;
    totals.set(`${COMBINED}|${native.currency}`, {
      source: COMBINED, currency: native.currency,
      grossRevenue: native.grossRevenue + other.grossRevenue,
      pstCharged: native.pstCharged + other.pstCharged,
      gstCharged: native.gstCharged + other.gstCharged,
      recordCount: native.recordCount + other.recordCount,
      basis: `Same-currency source sum after excluding linked Shopify mirrors and ${deferredRows.size} unresolved native invoice(s) for next-filing review; not a filed return.`
    });
  }
  exceptionMap.set("native_refund_tax_review|Ranch Power|CAD", {
    code: "native_refund_tax_review", source: NATIVE, currency: "CAD", count: 0, amount: null,
    message: "Native payment refunds and credits have no GST/PST allocation in this summary. Review credit notes and payment evidence before filing.", examples: []
  });
  const rank = { [NATIVE]: 0, [SHOPIFY]: 1, [COMBINED]: 2 };
  const presentedTotals = [...totals.values()].sort((a, b) => rank[a.source] - rank[b.source] || a.currency.localeCompare(b.currency))
    .map(row => ({ ...row, grossRevenue: amount(row.grossRevenue), pstCharged: amount(row.pstCharged), gstCharged: amount(row.gstCharged) }));
  const exceptions = [...exceptionMap.values()].map(row => ({ ...row, amount: row.amount == null ? null : amount(row.amount) }))
    .sort((a, b) => a.source.localeCompare(b.source) || a.code.localeCompare(b.code) || a.currency.localeCompare(b.currency));
  const dateFrom = filters.dateFrom, dateTo = filters.dateTo;
  const nextMonth = new Date(`${dateFrom.slice(0, 7)}-01T12:00:00Z`);
  nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
  nextMonth.setUTCDate(0);
  const fullMonth = dateFrom.endsWith("-01") && dateTo === nextMonth.toISOString().slice(0, 10);
  return { totals: presentedTotals, exceptions, sourceReconciliation,
    deferredInvoices: [...deferredRows.values()].map(({ grossRevenueCents, pstChargedCents, gstChargedCents, ...row }) => ({
      ...row, grossRevenue: amount(grossRevenueCents), pstCharged: amount(pstChargedCents),
      gstCharged: amount(gstChargedCents) })),
    sourceLinksReady, crossHistoryVerified: crossHistoryVerified === true,
    periodLabel: fullMonth ? dateFrom.slice(0, 7) : `${dateFrom} to ${dateTo}` };
}
